/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * DAS Topology Canvas
 * Main SVG canvas with pan/zoom and node/edge rendering
 */

import React, { useRef, useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { TopologyNode, Transform, TopologyEdge, NodePosition } from "./types";
import { computeLayout, buildNodeMap, getVisibleNodeIds } from "./layoutEngine";
import { DasNode, NodeFilters } from "./DasNode";
import { DasEdge, EdgeMarkers } from "./DasEdge";

interface DasTopologyCanvasProps {
  root: TopologyNode | null;
  collapsedNodeIds: Set<string>;
  selectedNodeId: string | null;
  showLabels: boolean;
  statusFilter: string;
  typeFilter: string;
  hcmLedColor: string;
  visibleNodeIdsForInventoryFilter: Set<string> | null;
  onNodeClick: (nodeId: string) => void;
  onNodeCollapse: (nodeId: string) => void;
  transform: Transform;
  onTransformChange: (transform: Transform) => void;
}

export const DasTopologyCanvas: React.FC<DasTopologyCanvasProps> = ({
  root,
  collapsedNodeIds,
  selectedNodeId,
  showLabels,
  statusFilter,
  typeFilter,
  hcmLedColor,
  visibleNodeIdsForInventoryFilter,
  onNodeClick,
  onNodeCollapse,
  transform,
  onTransformChange,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Helper function to check if node matches filters
  const nodeMatchesFilters = useCallback((node: TopologyNode): boolean => {
    // If inventory filter is active, ONLY check if node is in visible set
    // Don't apply toolbar filters on top as they would hide parent nodes
    if (visibleNodeIdsForInventoryFilter !== null) {
      const isVisible = visibleNodeIdsForInventoryFilter.has(node.id);
      if (!isVisible) {
        console.log('[DasTopologyCanvas] Node NOT in visible set:', {
          nodeId: node.id,
          nodeLabel: node.label,
          equipmentType: node.equipmentType
        });
      }
      return isVisible;
    }
    
    // No inventory filter - apply toolbar filters normally
    // Status filter (from toolbar)
    if (statusFilter !== "All" && node.status.toLowerCase() !== statusFilter.toLowerCase()) {
      return false;
    }
    
    // Type filter (from toolbar)
    if (typeFilter !== "All" && node.equipmentType !== typeFilter) {
      return false;
    }
    
    return true;
  }, [statusFilter, typeFilter, visibleNodeIdsForInventoryFilter]);

  // Compute layout
  const layout = computeLayout(root, collapsedNodeIds);
  const nodeMap = buildNodeMap(root);
  const visibleNodeIds = getVisibleNodeIds(root, collapsedNodeIds);
  
  // Log what's being rendered when inventory filter is active
  React.useEffect(() => {
    if (visibleNodeIdsForInventoryFilter !== null) {
      console.log('[DasTopologyCanvas] Inventory filter active, visible node IDs for filter:', 
        Array.from(visibleNodeIdsForInventoryFilter)
      );
      console.log('[DasTopologyCanvas] Layout positionedNodes count:', layout.positionedNodes.size);
      console.log('[DasTopologyCanvas] Visible node IDs from collapse state:', Array.from(visibleNodeIds));
    }
  }, [visibleNodeIdsForInventoryFilter, layout.positionedNodes.size, visibleNodeIds]);

  // Handle mouse wheel for zoom
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.max(0.1, Math.min(3, transform.scale * delta));
      
      onTransformChange({
        ...transform,
        scale: newScale,
      });
    },
    [transform, onTransformChange]
  );

  // Handle mouse down for panning
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button === 0) { // Left mouse button
      setIsPanning(true);
      setPanStart({ x: e.clientX - transform.x, y: e.clientY - transform.y });
    }
  };

  // Handle mouse move for panning
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isPanning) {
        onTransformChange({
          ...transform,
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y,
        });
      }
    },
    [isPanning, panStart, transform, onTransformChange]
  );

  // Handle mouse up to stop panning
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Add/remove event listeners
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    svg.addEventListener("wheel", handleWheel, { passive: false });
    
    return () => {
      svg.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel]);

  useEffect(() => {
    if (isPanning) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isPanning, handleMouseMove, handleMouseUp]);

  // Calculate viewBox dimensions with large padding to minimize size changes on expand
  const padding = 500; // Larger padding to accommodate expanded nodes
  const viewBoxWidth = Math.max(3000, layout.bounds.maxX - layout.bounds.minX + padding * 2);
  const viewBoxHeight = Math.max(2000, layout.bounds.maxY - layout.bounds.minY + padding * 2);
  const viewBoxX = layout.bounds.minX - padding;
  const viewBoxY = layout.bounds.minY - padding;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#020617", // slate-950 (mockup)
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
        onMouseDown={handleMouseDown}
        style={{
          cursor: isPanning ? "grabbing" : "grab",
        }}
      >
        <NodeFilters />
        <EdgeMarkers />
        
        <g transform={`translate(${transform.x}, ${transform.y}) scale(${transform.scale})`}>
          {/* Render edges first (behind nodes) */}
          {layout.edges.map((edge: TopologyEdge) => {
            const sourceNode = nodeMap.get(edge.source);
            const targetNode = nodeMap.get(edge.target);
            const sourcePos = layout.positionedNodes.get(edge.source);
            const targetPos = layout.positionedNodes.get(edge.target);

            if (!sourceNode || !targetNode || !sourcePos || !targetPos) {
              return null;
            }

            // Only render if both nodes are visible (not collapsed)
            if (!visibleNodeIds.has(edge.source) || !visibleNodeIds.has(edge.target)) {
              return null;
            }

            // Only render if both nodes match filters
            if (!nodeMatchesFilters(sourceNode) || !nodeMatchesFilters(targetNode)) {
              return null;
            }

            return (
              <DasEdge
                key={edge.id}
                edge={edge}
                sourceNode={sourceNode}
                targetNode={targetNode}
                sourcePos={sourcePos}
                targetPos={targetPos}
                isHovered={hoveredEdgeId === edge.id}
                onMouseEnter={setHoveredEdgeId}
                onMouseLeave={() => setHoveredEdgeId(null)}
              />
            );
          })}

          {/* Render nodes */}
          {(() => {
            const entries: Array<[string, NodePosition]> = [];
            layout.positionedNodes.forEach((position, nodeId) => {
              entries.push([nodeId, position]);
            });
            return entries.map(([nodeId, position]) => {
              const node = nodeMap.get(nodeId);
              if (!node || !visibleNodeIds.has(nodeId)) {
                return null;
              }

              // Apply filters
              if (!nodeMatchesFilters(node)) {
                return null;
              }

              return (
                <DasNode
                  key={nodeId}
                  node={node}
                  position={position}
                  isHovered={hoveredNodeId === nodeId}
                  isSelected={selectedNodeId === nodeId}
                  isCollapsed={collapsedNodeIds.has(nodeId)}
                  showLabel={showLabels}
                  hasChildren={node.children.length > 0}
                  hcmLedColor={hcmLedColor}
                  onMouseEnter={setHoveredNodeId}
                  onMouseLeave={() => setHoveredNodeId(null)}
                  onClick={onNodeClick}
                  onToggleCollapse={onNodeCollapse}
                />
              );
            });
          })()}
        </g>
      </svg>
      
      {/* Empty state */}
      {!root && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#9ca3af",
            textAlign: "center",
          }}
        >
          No topology data available
        </Box>
      )}
    </Box>
  );
};
